import { useState } from "react";
import axios from "axios";
import { Reveal } from "@/components/anim/Reveal";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Mail, MapPin, Send, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { SERVICES } from "@/data/solutions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = { name: "", company: "", email: "", phone: "", service: "", message: "" };

const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState({ state: "idle", message: "" }); // idle | submitting | success | error

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: "submitting", message: "" });
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        phone: form.phone.trim() || null,
        service: form.service || null,
        message: form.message.trim() || null,
      };
      const res = await axios.post(`${API}/contact`, payload, {
        headers: { "Content-Type": "application/json" },
        timeout: 15000,
      });
      if (res.status === 201 || res.status === 200) {
        setStatus({
          state: "success",
          message: "Thanks — a specialist will reach out within one business day.",
        });
        setForm(initialForm);
      } else {
        throw new Error("Unexpected response");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg).join(" · ")
        : typeof detail === "string"
          ? detail
          : "Something went wrong. Please try again or email us directly.";
      setStatus({ state: "error", message: msg });
    }
  };

  const submitting = status.state === "submitting";

  return (
    <div data-testid="page-contact">
      <section className="relative pt-24 pb-16 md:pt-32">
        <div className="fs-grid-bg" />
        <div className="fs-container relative grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-6">
            <Reveal><div className="fs-eyebrow">Contact</div></Reveal>
            <Reveal delay={0.1}>
              <h1 className="fs-heading text-4xl md:text-6xl mt-5">
                Let's design your next{" "}
                <span className="fs-gradient-text">infrastructure decision</span>{" "}
                together.
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-[color:var(--text-dim)] max-w-xl leading-relaxed">
                Tell us about your project or challenge. Our specialists will get
                back within one business day.
              </p>
            </Reveal>

            <Reveal delay={0.3}>
              <div className="mt-10 space-y-5">
                {[
                  { icon: Phone, label: "+91 86818 33444", href: "tel:+918681833444" },
                  { icon: Mail, label: "connect@foursquareinfo.com", href: "mailto:connect@foursquareinfo.com" },
                  { icon: MapPin, label: "No. 37, Govindan Street, Ayyavoo Naidu Colony, Aminjikarai, Chennai – 600029, Tamil Nadu, India" },
                ].map((c, i) => {
                  const Icon = c.icon;
                  const content = (
                    <span className="flex items-start gap-4">
                      <span className="fs-icon-tile shrink-0" style={{ width: 40, height: 40 }}><Icon size={16} /></span>
                      <span className="text-[color:var(--text-dim)] leading-relaxed">{c.label}</span>
                    </span>
                  );
                  return c.href ? (
                    <a key={i} href={c.href} className="block hover:text-[color:var(--cyan-bright)] transition-colors">
                      {content}
                    </a>
                  ) : (
                    <div key={i}>{content}</div>
                  );
                })}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-6">
            <Reveal delay={0.15}>
              <motion.form
                onSubmit={onSubmit}
                whileHover={{ y: -2 }}
                className="fs-card p-8"
                data-testid="contact-form"
                noValidate
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--text-muted)]">Name *</label>
                    <input required name="name" value={form.name} onChange={onChange} className="fs-input mt-2" data-testid="contact-name" disabled={submitting} />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--text-muted)]">Company</label>
                    <input name="company" value={form.company} onChange={onChange} className="fs-input mt-2" data-testid="contact-company" disabled={submitting} />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--text-muted)]">Email *</label>
                    <input required type="email" name="email" value={form.email} onChange={onChange} className="fs-input mt-2" data-testid="contact-email" disabled={submitting} />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--text-muted)]">Phone</label>
                    <input name="phone" value={form.phone} onChange={onChange} className="fs-input mt-2" data-testid="contact-phone" disabled={submitting} />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--text-muted)]">Service Interested In</label>
                  <select name="service" value={form.service} onChange={onChange} className="fs-input mt-2" data-testid="contact-service" disabled={submitting}>
                    <option value="">— Select a service —</option>
                    {SERVICES.map((s) => (
                      <option key={s.title} value={s.title}>{s.title}</option>
                    ))}
                    <option value="Other">Other / Not sure yet</option>
                  </select>
                </div>

                <div className="mt-4">
                  <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--text-muted)]">Message</label>
                  <textarea rows={5} name="message" value={form.message} onChange={onChange} className="fs-input mt-2" data-testid="contact-message" disabled={submitting} />
                </div>

                <div className="mt-6 flex items-center gap-4 flex-wrap">
                  <button
                    type="submit"
                    className="fs-btn-primary"
                    data-testid="contact-submit"
                    disabled={submitting}
                    style={submitting ? { opacity: 0.75, cursor: "not-allowed" } : undefined}
                  >
                    {submitting ? (
                      <><Loader2 size={16} className="animate-spin" /> Sending…</>
                    ) : (
                      <><Send size={16} /> Send Message</>
                    )}
                  </button>

                  <AnimatePresence mode="wait">
                    {status.state === "success" && (
                      <motion.div
                        key="ok"
                        initial={{ opacity: 0, x: -6 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 6 }}
                        className="flex items-center gap-2 text-sm"
                        style={{ color: "#7be0b5" }}
                        role="status"
                        data-testid="contact-success"
                      >
                        <CheckCircle2 size={16} />
                        <span>{status.message}</span>
                      </motion.div>
                    )}
                    {status.state === "error" && (
                      <motion.div
                        key="err"
                        initial={{ opacity: 0, x: -6 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 6 }}
                        className="flex items-center gap-2 text-sm"
                        style={{ color: "#f4a4a4" }}
                        role="alert"
                        data-testid="contact-error"
                      >
                        <AlertCircle size={16} />
                        <span>{status.message}</span>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.form>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
